/**
 * Saved-measures overlay — renders every pinned measurement (saved rulers) as a
 * static, dimmer overlay: the dashed bearing line, its dashed range circle, and
 * the per-leg distance/bearing labels. Stays drawn while the user does other
 * things. Each feature carries its measurement id (`mid`) so a tap can delete it.
 */
import type { Map as MaplibreMap, GeoJSONSource } from 'maplibre-gl';
import type { Feature, FeatureCollection } from 'geojson';
import { rhumbDistanceNm, rhumbBearingDeg, rhumbDestination, formatDistance, formatBearing } from '@/nav';
import { rangeCircle } from './measureLayer';
import { getPalette } from './style/palettes';
import type { Palette } from '@/store';
import type { SavedMeasurement } from '@/store/savedMeasurements';

const SOURCE_ID = 'saved-measures';

function build(items: SavedMeasurement[]): FeatureCollection {
  const features: Feature[] = [];
  for (const m of items) {
    const pts = m.points;
    if (pts.length < 2) continue;
    features.push({
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: pts },
      properties: { kind: 'sline', mid: m.id },
    });
    const radius = rhumbDistanceNm(pts[0], pts[pts.length - 1]);
    if (radius > 0) {
      features.push({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: rangeCircle(pts[0], radius) },
        properties: { kind: 'scircle', mid: m.id },
      });
    }
    for (let i = 1; i < pts.length; i++) {
      const a = pts[i - 1];
      const b = pts[i];
      const d = rhumbDistanceNm(a, b);
      const brg = rhumbBearingDeg(a, b);
      const mid = rhumbDestination(a, brg, d / 2);
      features.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: mid },
        properties: { kind: 'slabel', mid: m.id, label: `${formatDistance(d)} · ${formatBearing(brg)}` },
      });
    }
  }
  return { type: 'FeatureCollection', features };
}

export function ensureSavedMeasuresLayer(map: MaplibreMap, palette: Palette): void {
  const p = getPalette(palette);
  if (!map.getSource(SOURCE_ID)) {
    map.addSource(SOURCE_ID, { type: 'geojson', data: build([]) });
  }
  if (!map.getLayer('saved-measure-circle')) {
    map.addLayer({
      id: 'saved-measure-circle',
      type: 'line',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'scircle'],
      paint: { 'line-color': p.UI_ACCENT, 'line-width': 1.2, 'line-dasharray': [3, 3], 'line-opacity': 0.4 },
    });
  }
  if (!map.getLayer('saved-measure-line')) {
    map.addLayer({
      id: 'saved-measure-line',
      type: 'line',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'sline'],
      paint: { 'line-color': p.UI_ACCENT, 'line-width': 1.6, 'line-dasharray': [2, 1.5], 'line-opacity': 0.5 },
    });
  }
  if (!map.getLayer('saved-measure-label')) {
    map.addLayer({
      id: 'saved-measure-label',
      type: 'symbol',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'slabel'],
      layout: {
        'text-field': ['get', 'label'],
        'text-font': ['Open Sans Regular'],
        'text-size': 10,
        'text-allow-overlap': true,
        'text-offset': [0, -0.8],
      },
      paint: { 'text-color': p.UI_ACCENT, 'text-halo-color': p.UI_BG, 'text-halo-width': 1.4, 'text-opacity': 0.7 },
    });
  }
}

export function updateSavedMeasuresLayer(map: MaplibreMap, items: SavedMeasurement[]): void {
  const src = map.getSource(SOURCE_ID);
  if (src) (src as GeoJSONSource).setData(build(items));
}
